import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { ReactComponent as Home } from "../../../assets/svg/home.svg";
import { ReactComponent as Contracts } from "../../../assets/svg/contracts.svg";
import { ReactComponent as Clients } from "../../../assets/svg/clients-alt.svg";
import { ReactComponent as Applications } from "../../../assets/svg/applications.svg";
import { ReactComponent as Wallet } from "../../../assets/svg/wallet.svg";
import { ReactComponent as Transactions } from "../../../assets/svg/transactions.svg";
import { ReactComponent as Settings } from "../../../assets/svg/settings.svg";
import { ReactComponent as Updates } from "../../../assets/svg/updates.svg";

import styles from "./sidebar.module.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../../setup/constants";
import { logOut } from "../../../setup/context/authContext";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { LogoutButton } from "../../../components/logout";
import { BUTTONS } from "../../../components/navbar/constants";
import { getBasicUserDetailsById } from "../../../setup/api/services/user";
import { AuthConfig } from "../../../setup/context/config/authConfig";
import { IUserInfoResponseData } from "../../../setup/api/services/user/models";
import { getOrganization } from "../../../setup/api/services/organization";
import { useOrganizationCode } from "../../../setup/util/extractOrganizationCode";

const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<IUserInfoResponseData | undefined>(
    undefined
  );
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const userToken = AuthConfig.getToken();
  const [organization, setOrganization] = useState<{
    name?: string;
    logoUrl?: string;
  } | null>(null);

  const organizationCode = useOrganizationCode(userToken) || undefined;

  useEffect(() => {
    const getUserDetails = async () => {
      const userId = AuthConfig.getUserInfo()?.id;

      const result = await getBasicUserDetailsById(userId);

      setUserInfo(result?.data);
      AuthConfig.setUserInfo({
        ...result?.data,
        id: result?.data?.id?.toString(),
        userIdentification: result?.data?.userIdentification?.toString() || "",
      });
    };

    getUserDetails();
  }, []);

  const fetchOrganization = useCallback(async () => {
    if (!organizationCode) return;
    const response = await getOrganization(organizationCode as string);
    setOrganization(response.data);
  }, [organizationCode]);

  useEffect(() => {
    if (organizationCode) {
      fetchOrganization();
    }
  }, [fetchOrganization, organizationCode]);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const onSignOutclick = () => {
    logOut();
    handleMenuClose();
    window.location.reload();
  };

  const navLinks = useMemo(() => {
    return [
      {
        id: 1,
        title: t("lbl_business_home"),
        icon: <Home />,
        active: location.pathname === ROUTES.HOME,
        route: ROUTES.HOME,
        activeClassName: styles.homeActive,
      },
      {
        id: 2,
        title: t("lbl_projects"),
        icon: <Contracts />,
        active: location.pathname === ROUTES.CONTRACTS_LIST,
        route: ROUTES.CONTRACTS_LIST,
        activeClassName: styles.contractsActive,
      },
      {
        id: 3,
        title: t("lbl_customers"),
        icon: <Clients />,
        active: location.pathname?.includes(ROUTES.USERS_MANAGEMENT),
        route: ROUTES.USERS_MANAGEMENT,
        activeClassName: styles.customersActive,
      },
      {
        id: 4,
        title: t("lbl_latest_updates"),
        icon: <Updates />,
        active: location.pathname === ROUTES.LATEST_UPDATES,
        route: ROUTES.LATEST_UPDATES,
        activeClassName: styles.updatesActive,
      },
      // {
      //   id: 5,
      //   title: t("lbl_applications"),
      //   icon: <Applications />,
      //   activeClassName: styles.applicationsActive,
      // },
      {
        id: 5,
        title: t("lbl_money_transactions"),
        icon: <Transactions />,
        active: location.pathname === ROUTES.MONEY_TRANSACTIONS,
        route: ROUTES.MONEY_TRANSACTIONS,
        activeClassName: styles.moneyActive,
      },
      {
        id: 6,
        title: t("lbl_balance"),
        icon: <Wallet />,
        active: location.pathname === ROUTES.BALANCE,
        route: ROUTES.BALANCE,
        activeClassName: styles.balanceActive,
      },
      {
        id: 7,
        title: t("lbl_settings"),
        icon: <Settings />,
        active: location.pathname === ROUTES.SETTINGS,
        route: ROUTES.SETTINGS,
        activeClassName: styles.settingsActive,
      },
    ];
  }, [location.pathname, t]);

  const sidebarClassName = classNames(styles.sidebar, {
    [styles.sidebarClosed]: !sidebarOpen,
  });

  const menuId = "primary-account-menu";
  // TODO: Add the new menu design
  const renderMenu = (
    <Menu
      dir="rtl"
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      style={{ width: "320px" }}
      open={isMenuOpen}
      onClose={handleMenuClose}>
      <MenuItem onClick={onSignOutclick}>
        <LogoutButton />
        {BUTTONS.LOGOUT}
      </MenuItem>
    </Menu>
  );

  return (
    <div className={sidebarClassName}>
      <div
        onClick={() => {
          setSidebarOpen((prev) => !prev);
        }}
        className={styles.toggle}
        style={{
          transform: sidebarOpen ? "rotate(180deg)" : "",
        }}>
        <img
          src="/assets/images/svg/arrow2.svg"
          alt="arrow"
          className={styles.toggleIcon}
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          gap: "12px",
        }}>
        {sidebarOpen && (
          <div className={styles.header}>
            <p className={styles.companyLabel}>{t("lbl_company")}</p>

            <h5 className={styles.companyName}>{organization?.name}</h5>
          </div>
        )}
        {/* {organization?.logoUrl && (
          <img
            src={organization?.logoUrl}
            alt="logo"
            style={{ width: "58px", height: "32px" }}
          />
        )} */}
      </div>

      <div>
        {navLinks?.map((navLink) => {
          const itemClassName = classNames(styles.navItem, {
            [styles.navItemClosed]: !sidebarOpen,
            [styles.navItemActive]: navLink?.active,
            [navLink?.activeClassName]: navLink?.active,
          });
          const itemInnerClassName = classNames(styles.navItemInner, {
            [styles.navItemInnerCollapsed]: !sidebarOpen,
          });
          const titleClassName = classNames(styles.navTitle, {
            [styles.navTitleActive]: navLink?.active,
          });

          return (
            <div
              key={navLink?.id}
              className={itemClassName}
              onClick={() => navigate(navLink?.route ?? "")}>
              <div className={itemInnerClassName}>
                {navLink?.icon}
                {sidebarOpen && (
                  <p className={titleClassName}>{navLink?.title}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div
        aria-label="account of current user"
        aria-controls={menuId}
        aria-haspopup="true"
        onClick={handleProfileMenuOpen}
        className={classNames(
          styles.flexRow,
          styles.alignCenter,
          styles.justifyStart,
          styles.gap8,
          styles.user
        )}>
        <img
          src={userInfo?.profile?.photoUrl || "/assets/images/svg/user.svg"}
          alt="user"
          className={styles.userImage}
        />
        {sidebarOpen && (
          <div className={styles.userTextCol}>
            <h5 className={styles.userName}>{userInfo?.profile?.fullName}</h5>
            <p className={styles.userPhone}>{t("lbl_executive")}</p>
          </div>
        )}
      </div>
      {renderMenu}
    </div>
  );
};

export default Sidebar;
